<template>
    <div :key="iteration">
        <div v-show="isLoading" class="widget-overlay">
            <div class="loader">
                Loading...
            </div>
        </div>

        <h2 class="center" v-if="hasStarted() && ! hasFinished()">
            Application type: {{ applicationFormValues?.applicationType?.toUpperCase() }}
        </h2>

        <progress-bar
            v-if="hasStarted() && ! hasFinished()"
            :current="currentPage"
            :total="numberOfPages">
        </progress-bar>

        <investment-type
            v-if="! hasStarted()"
            @emit-form-started="start">
        </investment-type>

        <terms-condition
            v-if="hasStarted() && !hasFinished() && currentPage === 1"
            @event-investment-terms-condition="handleCurrentPage">
        </terms-condition>

        <select-investor
            v-if="hasStarted() && !hasFinished() && currentPage === 2"
            @emit-selected-investor="handleCurrentPage"
            @emit-clear-investor-details="unSetInvestorDetails"
            @is-loading="handleLoadingDiv"
            :application-form-values="applicationFormValues"
        >
        </select-investor>

        <investor-details
            v-if="hasStarted() && !hasFinished() && currentPage === 3"
            @emit-investor-details="handleCurrentPage"
            @is-loading="handleLoadingDiv"
            :new-investor-flag="newInvestorFlag"
            :application-form-values="applicationFormValues"
        >
        </investor-details>

        <lump-sum
            v-if="hasStarted() && !hasFinished() && currentPage === 4"
            :application-form-values="applicationFormValues"
            @emit-lump-sum="handleCurrentPage"
            @is-loading="handleLoadingDiv"
            @emit-share-class="populateShareClass"
        >
        </lump-sum>

        <review
            v-if="hasStarted() && !hasFinished() && currentPage === 5"
            @event-submit-application="reviewPage"
            @is-loading="handleLoadingDiv"
            :application-form-values="applicationFormValues"
            :share-classes="shareClasses"
        >
        </review>

        <section v-if="isCompleted" class="finish-section">
            <p class="center">
                Thank you for completing this application.
            </p>
            <button type="button" class="button button-large" @click.stop="restart">
                <span>New Application</span>
                <i class="fa-light fa-file-chart-line" aria-hidden="true"></i>
            </button>
            <p v-if="allowReport" class="center">
                <button type="button" class="button button-large" @click.stop="requestReport($event)">
                    <span>Generate Report</span>
                    <i class="fa-light fa-file-chart-line" aria-hidden="true"></i>
                </button>
            </p>
        </section>
    </div>
</template>

<script>
    import Widget from '../Widget.vue';
    import MargettsApi from '../../modules/margetts-api';
    import ProgressBar from "../ProgressBar.vue";
    import InvestmentType from "./Pages/InvestmentType.vue";
    import TermsCondition from "./Pages/TermsCondition.vue";
    import SelectInvestor from "./Pages/SelectInvestor.vue";
    import InvestorDetails from "./Pages/InvestorDetails.vue";
    import LumpSum from "./Pages/LumpSum.vue";
    import Review from "./Pages/Review.vue";

    export default {
        extends: Widget,
        components: {InvestmentType, TermsCondition, ProgressBar, SelectInvestor, InvestorDetails, LumpSum, Review},
        data() {
            return {
                iteration: 0,
                isLoading: false,
                newInvestorFlag: false,
                applicationFormValues : {},
                shareClasses: {},
                investor: null,
                lumpSum: null,
                numberOfPages: 5,
                currentPage: 0,
                currentPageId: 0,
                currentAnswer: null,
                isCompleted: false,
                summary: null,
                allowReport: false,
                partial: '',
            }
        },

        created() {
            this.iteration += 1;
        },

        methods: {
            handleLoadingDiv(isLoading) {
                this.isLoading = isLoading;
            },

            start(appType) {
                this.block();
                if (!this.applicationFormValues.hasOwnProperty('applicationFormID')) {
                    MargettsApi.post('investment-application/start', this.createQueryStringFromParameters({
                        user: 'userId'
                    }, false), response => {
                        if ( ! response || ! response.result) {
                            return MargettsApi.throwNoResponseError();
                        }

                        if (response.result !== 'success') {
                            return this.throwApiError(response.messages);
                        }

                        const data = response.data;
                        this.applicationFormValues = { applicationFormID: data.application_id, applicationType: appType, ...this.applicationFormValues};
                    }).catch(error => {
                        return MargettsApi.throwError(error);
                    });
                }

                this.currentPage = 1;
                this.unblock();
            },

            handleCurrentPage(page) {
                if (typeof page === 'number') {
                    this.currentPage = page;
                }

                if (typeof page === 'object') {
                    this.currentPage = page.page;
                    this.newInvestorFlag = page?.newInvestorFlag ?? this.newInvestorFlag;
                    if (page.investorDetails) {
                        this.setInvestorDetails(page.investorDetails);
                    }
                    if (page.lumpSum) {
                        this.applicationFormValues.lumpSum = page.lumpSum;
                    }
                }
            },

            hasStarted() {
                return this.currentPage > 0;
            },

            hasFinished() {
                return this.isCompleted;
            },

            restart() {
                this.applicationFormValues = {};
                this.isCompleted = false;
                this.currentPage = 0;
                this.unSetInvestorDetails()
                this.shareClassesValues = [];
                this.lumpSum = null;
            },

            setInvestorDetails(data) {
                this.applicationFormValues.investorDetails = {...data};
            },

            unSetInvestorDetails() {
                this.investor = null;
                this.applicationFormValues.investorDetails = {};
            },

            populateShareClass(data) {
                this.shareClasses = data;
            },

            async reviewPage(status) {
                if (!status) {
                    this.currentPage = 4;
                    return;
                }

                this.block();
                let formValues = {application_id: this.applicationFormValues.applicationFormID};
                await MargettsApi.post('investment-application/submit', this.createQueryStringFromParameters(formValues, false), response => {
                    if (!response || !response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                }).catch(error => {
                    MargettsApi.throwError(error);
                });

                this.unblock();
                this.isCompleted = true
            },

            requestReport(event) {
                if ( ! this.allowReport) {
                    return;
                }

                this.block();

                const parameters = {
                    'survey': this.survey.toString()
                };

                MargettsApi.post('risk-profiler/report', this.createQueryStringFromParameters(parameters, false), response => {
                    if ( ! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    if (data.file) {
                        this.awaitFileInBackground(data.file.name, data.file.path);
                    }

                    if (response.messages) {
                        flash(response.messages.join('<br>'), 'success');
                    }

                    this.unblock();
                }).catch(error => {
                    MargettsApi.throwError(error);
                });
            },
        },
    }
</script>

<style>
.finish-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.share-class-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0.5em;

}

.shareClass-row-col-6 > .form-group:last-of-type {
    flex-direction: row !important;
    justify-content: flex-start !important;
    margin-top: 2em !important;
}

.app-title {
    margin: 0 0 10px 0 !important;
}
</style>
